import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { FileText, Library, Upload, Check, RefreshCw, Sparkles, Loader2 } from "lucide-react";

// Same accept spec as ProfileNew.jsx (PDF / DOCX / DOC via server-side
// _extract_pd_text). Kept close to the picker for easy audit.
const ACCEPT_EXT = ".pdf,.docx,.doc";
const ACCEPT_MIME = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];
function extOf(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

/**
 * Reusable PD source picker: user chooses to upload a new file or pick one
 * from their saved-PD library, then clicks "Attach PD & generate tasks".
 * Calls `onAttached(response)` after the backend confirms — the caller is
 * responsible for reloading the profile / navigating.
 *
 * @param {string} profileId — target profile id.
 * @param {(res: any) => void} onAttached
 */
export default function AttachPDPicker({ profileId, onAttached }) {
  const inputRef = useRef(null);
  const [source, setSource] = useState("upload");
  const [file, setFile] = useState(null);
  const [savedPds, setSavedPds] = useState([]);
  const [pdsLoading, setPdsLoading] = useState(false);
  const [pdsLoaded, setPdsLoaded] = useState(false);
  const [pickedPdId, setPickedPdId] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSavedPds = React.useCallback(() => {
    setPdsLoading(true);
    api.get("/pd-generator/documents")
      .then((r) => setSavedPds(Array.isArray(r.data) ? r.data : []))
      .catch((err) => toast.error(apiError(err, "Failed to load saved PDs")))
      .finally(() => { setPdsLoading(false); setPdsLoaded(true); });
  }, []);

  useEffect(() => {
    if (source !== "library" || pdsLoaded || pdsLoading) return;
    loadSavedPds();
  }, [source, pdsLoaded, pdsLoading, loadSavedPds]);

  const onFilePick = (f) => {
    if (!f) { setFile(null); return; }
    const ext = extOf(f.name);
    if (!["pdf", "docx", "doc"].includes(ext)) {
      toast.error("Only PDF, DOCX, or DOC files are supported");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (source === "upload" && !file) { toast.error("Select a PDF, DOCX, or DOC file"); return; }
    if (source === "library" && !pickedPdId) { toast.error("Pick a saved PD from the library"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (source === "upload") fd.append("pdf", file);
      else fd.append("pd_document_id", pickedPdId);
      const r = await api.post(`/profiles/${profileId}/pd`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`PD attached · ${r.data.tasks.length} tasks generated`);
      onAttached?.(r.data);
    } catch (err) {
      toast.error(apiError(err, "Failed to attach PD"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="attach-pd-picker">
      {/* Source toggle */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="tablist" aria-label="Attach PD source">
        <button
          type="button"
          onClick={() => setSource("upload")}
          className={`p-3 border-2 text-left transition ${source === "upload" ? "border-[#FF4F00] bg-[#FFF5F0]" : "border-[#DDD] bg-white hover:border-[#111]"}`}
          data-testid="attach-source-upload"
          disabled={busy}
          role="tab"
          aria-selected={source === "upload"}
        >
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1 flex items-center gap-1">
            <Upload size={11} /> Upload file
          </div>
          <div className="font-display font-semibold text-sm">PDF, DOCX, or DOC</div>
        </button>
        <button
          type="button"
          onClick={() => setSource("library")}
          className={`p-3 border-2 text-left transition ${source === "library" ? "border-[#FF4F00] bg-[#FFF5F0]" : "border-[#DDD] bg-white hover:border-[#111]"}`}
          data-testid="attach-source-library"
          disabled={busy}
          role="tab"
          aria-selected={source === "library"}
        >
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1 flex items-center gap-1">
            <Library size={11} /> Saved library
          </div>
          <div className="font-display font-semibold text-sm">Pick a saved PD</div>
        </button>
      </div>

      {source === "upload" && (
        <div
          onClick={() => !busy && inputRef.current?.click()}
          className={`border border-dashed border-[#111] p-8 text-center cursor-pointer transition ${busy ? "pointer-events-none opacity-60" : "hover:bg-[#F4F4F0]"}`}
          data-testid="attach-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept={`${ACCEPT_EXT},${ACCEPT_MIME.join(",")}`}
            onChange={(e) => onFilePick(e.target.files?.[0] || null)}
            className="hidden"
            data-testid="attach-file-input"
          />
          <div className="font-mono-cal text-[11px] uppercase tracking-widest text-[#666] mb-2">
            [ PDF · DOCX · DOC ]
          </div>
          <div className="font-display font-bold text-lg">{file ? file.name : "Click to select a document"}</div>
          {file && <div className="mt-1 text-xs text-[#666]">{(file.size / 1024).toFixed(1)} KB · {extOf(file.name).toUpperCase()}</div>}
        </div>
      )}

      {source === "library" && (
        <div data-testid="attach-library-picker">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">
              [ SAVED PDS ]
            </div>
            <button
              type="button"
              onClick={loadSavedPds}
              disabled={pdsLoading || busy}
              className="cal-btn cal-btn-ghost text-xs !py-1 !px-2"
              data-testid="attach-library-refresh"
              aria-label="Refresh saved PDs"
            >
              <RefreshCw size={12} className={pdsLoading ? "animate-spin" : ""} />
            </button>
          </div>
          {pdsLoading ? (
            <div className="text-sm text-[#666]">Loading your saved PDs…</div>
          ) : savedPds.length === 0 ? (
            <div className="cal-card p-4 bg-[#F4F4F0] text-sm" data-testid="attach-library-empty">
              No items in the Library yet.{" "}
              <Link to="/pd-generator" className="underline font-semibold">Draft one with the PD Generator</Link>{" "}
              and it&apos;ll appear here.
            </div>
          ) : (
            <ul className="space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="attach-library-list">
              {savedPds.map((d) => {
                const active = pickedPdId === d.id;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => setPickedPdId(d.id)}
                      className={`w-full text-left p-3 border transition flex items-start gap-3 ${active ? "bg-[#111] text-white border-[#111]" : "bg-white border-[#DDD] hover:border-[#111]"}`}
                      data-testid={`attach-library-pick-${d.id}`}
                      disabled={busy}
                    >
                      <div className={`mt-0.5 flex-none w-5 h-5 border-2 flex items-center justify-center ${active ? "border-white bg-white text-[#111]" : "border-[#111]"}`}>
                        {active && <Check size={12} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold text-sm truncate">{d.job_title}</div>
                        <div className={`text-xs truncate ${active ? "text-[#DDD]" : "text-[#666]"}`}>{d.department}</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="cal-btn"
          data-testid="attach-pd-submit"
        >
          {busy ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Attaching…</> : <><Sparkles size={14} className="inline mr-1" /> Attach PD & generate tasks</>}
        </button>
      </div>
      <div className="flex items-center gap-2 text-xs font-mono-cal text-[#666]">
        <FileText size={11} /> New tasks replace any existing ones on this profile.
      </div>
    </div>
  );
}
